<template>
  <div class="container-fluid">
    <div class="row" id="app">
      <!-- Inputs Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <TopbarNav />
        <hr/>

        <!-- Section 1: Concrete Properties -->
        <h5>Concrete Properties</h5>
        <div class="row">
          <div class="col-6">
            <label for="phi_c">Resistance Factor for Concrete, φ<sub>c</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="phi_c" v-model="phi_c" step="0.01" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="fc_prime">Compressive Strength, f'<sub>c</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="fc_prime" v-model="fc_prime" step="1" /><span>MPa</span>
          </div>
        </div>
        <hr/>

        <!-- Section 2: Forces -->
        <h5>Factored Forces/Moments</h5>
        <div class="row">
          <div class="col-6">
            <label for="Fz">Fz:</label>
          </div>
          <div class="col-6">
            <input
              type="number"
              name="Fz"
              v-model="Fz"
            /><span>kN</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="Mx">Mx:</label>
          </div>
          <div class="col-6">
            <input
              type="number"
              name="Mx"
              v-model="Mx"
            /><span>kN.m</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="Mx">My:</label>
          </div>
          <div class="col-6">
            <input
              type="number"
              name="My"
              v-model="My"
            /><span>kN.m</span>
          </div>
        </div>
        <hr/>

        <!-- Section 3: Slab Properties -->
        <h5>Slab Properties</h5>
        <div class="row">
          <div class="col-6">
            <label for="d">Effective Depth, d:</label>
          </div>
          <div class="col-6">
            <input type="number" name="d" v-model="d" step="1" /><span>mm</span>
          </div>
        </div>
        <hr/>

        <!-- Section 4: Column Dimensions -->
        <h5>Column Dimensions</h5>
        <div class="row">
          <div class="col-6">
            <label for="column_width">Width, b<sub>1</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="column_width" v-model="column_width" step="1" /><span>mm</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="column_depth">Depth, b<sub>2</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="column_depth" v-model="column_depth" step="1" /><span>mm</span>
          </div>
        </div>
        <hr/>

        <!-- Section 5: Shear Stress Resistance Parameters -->
        <h5>Shear Stress Resistance Parameters</h5>
        <div class="row">
          <div class="col-6">
            <label for="lambda">&lambda;:</label>
          </div>
          <div class="col-6">
            <input type="number" name="lambda" v-model="lambda" step="0.01" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="lambda_s">&lambda;<sub>s</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="lambda_s" v-model="lambda_s" step="0.01" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="alpha_s">&alpha;<sub>s</sub>:</label>
          </div>
          <div class="col-6">
            <select name="alpha_s" v-model="alpha_s">
              <option value="4">4 - Interior columns</option>
              <option value="3">3 - Edge columns</option>
              <option value="2">2 - Corner columns</option>
            </select>
          </div>
        </div>
        <div class="row" v-if="alpha_s === '3' || alpha_s === '2'">
          <div class="col-6">
            <label for="excluded_side">Excluded Side:</label>
          </div>
          <div class="col-6">
            <select name="excluded_side" v-model="excluded_side">
              <template v-if="alpha_s === '3'">
                <option value="N">N</option>
                <option value="E">E</option>
                <option value="S">S</option>
                <option value="W">W</option>
              </template>
              <template v-if="alpha_s === '2'">
                <option value="NE">NE</option>
                <option value="NW">NW</option>
                <option value="SE">SE</option>
                <option value="SW">SW</option>
              </template>
            </select>
          </div>
        </div>
        <hr/>
      </div>

      <!-- Report Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <h4>Punching Shear Calculation</h4>

        <!-- Shear Stress -->
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'v_f = \\frac{F_z}{A_c} \\pm \\frac{\\gamma_{vx} M_x c_y}{J_{cx}} \\pm \\frac{\\gamma_{vy} M_y c_x}{J_{cy}}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">ACI 318-19 22.6.4.1</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'v_f = ' + vf_axial.toFixed(2) + ' \\pm ' + vf_Mx.toFixed(2) + ' \\pm ' + vf_My.toFixed(2) + ' = ' + vf.toFixed(2) + ' \\text{ MPa}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Shear stress</p>
          </div>
        </div>

        <!-- Check v_f vs v_r -->
        <div class="row mt-2">
          <div class="col-6">
            <p class="fst-italic">
              <span v-if="vf > vr">
                v<sub>f</sub> = {{ vf.toFixed(2) }} MPa > v<sub>r</sub> = {{ vr.toFixed(2) }} MPa
              </span>
              <span v-else>
                v<sub>f</sub> = {{ vf.toFixed(2) }} MPa ≤ v<sub>r</sub> = {{ vr.toFixed(2) }} MPa
              </span>
            </p>
          </div>
          <div class="col-6">
            <p class="fst-italic">
              <span v-if="vf > vr" class="text-danger">NG</span>
              <span v-else class="text-success">OK</span>
            </p>
          </div>
        </div>
        <div class="row" v-if="vf > vr">
          <div class="col-12">
            <p class="fst-italic">
              Punching shear fails, shear reinforcement is required.
            </p>
          </div>
        </div>
        <hr/>
        
        <PunchingShearDiagram
          :column_width="column_width"
          :column_depth="column_depth"
          :d="d"
          :shear_perimeter_polygon="shear_perimeter_polygon"
          :segment_stresses="segment_stresses"
          :cx="cx"
          :cy="cy"
        />
        <hr/>
        
        <!-- Shear Perimeter Coordinates -->
        <div class="row">
          <h5>Shear Perimeter Coordinates & Stresses</h5>
          <div class="col-12">
            <table class="table table-sm engsheet-table text-center">
              <thead>
                <tr class="border-bottom">
                  <th>Segment</th>
                  <th>x<sub>i</sub> (mm)</th>
                  <th>y<sub>i</sub> (mm)</th>
                  <th>x<sub>j</sub> (mm)</th>
                  <th>y<sub>j</sub> (mm)</th>
                  <th>L (mm)</th>
                  <th>Excluded</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="seg in shear_perimeter_polygon" :key="seg.label" :class="seg.excluded ? 'table-danger' : ''">
                  <td>{{ seg.label }}</td>
                  <td>{{ seg.point_i.x.toFixed(1) }}</td>
                  <td>{{ seg.point_i.y.toFixed(1) }}</td>
                  <td>{{ seg.point_j.x.toFixed(1) }}</td>
                  <td>{{ seg.point_j.y.toFixed(1) }}</td>
                  <td>{{ seg.L.toFixed(1) }}</td>
                  <td>{{ seg.excluded ? '✕' : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Shear Perimeter -->
         <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'A_c = \\sum Ld = ' + Ac.toExponential(1) + ' \\text{ mm}^2'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Shear perimeter area</p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'J_{cx} = \\sum \\frac{Ld}{3}(y_i^2 + y_i y_j + y_j^2) = ' + Jcx.toExponential(1) + ' \\text{ mm}^4'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Polar moment of inertia about x-axis</p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'J_{cy} = \\sum \\frac{Ld}{3}(x_i^2 + x_i x_j + x_j^2) = ' + Jcy.toExponential(1) + ' \\text{ mm}^4'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Polar moment of inertia about y-axis</p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\gamma_{fx} = \\frac{1}{1 + \\frac{2}{3}\\sqrt{\\frac{b_1}{b_2}}} = ' + gamma_fx.toFixed(3)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Fraction of moment transferred by flexure, x-direction</p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\gamma_{vx} = 1 - \\gamma_{fx} = ' + gamma_vx.toFixed(3)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Fraction of moment transferred by shear, x-direction</p>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-6">
            <KaTeXComponent :expression="'\\gamma_{fy} = \\frac{1}{1 + \\frac{2}{3}\\sqrt{\\frac{b_2}{b_1}}} = ' + gamma_fy.toFixed(3)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Fraction of moment transferred by flexure, y-direction</p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\gamma_{vy} = 1 - \\gamma_{fy} = ' + gamma_vy.toFixed(3)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Fraction of moment transferred by shear, y-direction</p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'c_x = \\frac{\\sum A x}{\\sum A} = ' + cx.toFixed(1) + ' \\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Centroid x-coordinate of shear perimeter</p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'c_y = \\frac{\\sum Ay}{\\sum A} = ' + cy.toFixed(1) + ' \\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Centroid y-coordinate of shear perimeter</p>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12">
            <table class="table table-sm engsheet-table text-center">
              <thead>
                <tr>
                  <th>Corner</th>
                  <th>v<sub>Fz</sub> (MPa)</th>
                  <th>v<sub>Mx</sub> (MPa)</th>
                  <th>v<sub>My</sub> (MPa)</th>
                  <th>v<sub>f</sub> (MPa)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pt in corner_stresses" :key="pt.label">
                  <td>{{ pt.label }}</td>
                  <td>{{ pt.v_Fz.toFixed(3) }}</td>
                  <td>{{ pt.v_Mx.toFixed(3) }}</td>
                  <td>{{ pt.v_My.toFixed(3) }}</td>
                  <td><strong>{{ (pt.v_Fz + pt.v_Mx + pt.v_My).toFixed(3) }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'b_0 = 2(d/2 + c_1 + d/2) + 2(d/2 + c_2 + d/2)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'b_0 = ' + b0.toFixed(2) + ' \\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Shear perimeter</p>
          </div>
        </div> -->
        <hr/>

        <!-- Shear Stress Resistance -->
        <h5>Shear Stress Resistance [CSA A23.3-19 13.3.4.1]</h5>

        <!-- beta_c -->
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\beta_c = \\frac{\\max(c_1, c_2)}{\\min(c_1, c_2)}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\beta_c = ' + beta_c.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Ratio of long to short dimension</p>
          </div>
        </div>

        <!-- Equation 13.5 -->
        <div class="row mt-2">
          <div class="col-6">
            <KaTeXComponent :expression="'v_r = \\left(1 + \\frac{2}{\\beta_c}\\right) 0.19 \\lambda_s \\lambda \\phi_c \\min(\\sqrt{f\'_c}, 8)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">[Eq. 13.5]</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'v_r = ' + vr_13_5.toFixed(2) + ' \\text{ MPa}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>

        <!-- Equation 13.6 -->
        <div class="row mt-2">
          <div class="col-6">
            <KaTeXComponent :expression="'v_r = \\left(\\frac{\\alpha_s d}{b_0} + 0.19\\right) \\lambda_s \\lambda \\phi_c \\min(\\sqrt{f\'_c}, 8)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">[Eq. 13.6]</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'v_r = ' + vr_13_6.toFixed(2) + ' \\text{ MPa}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>

        <!-- Equation 13.7 -->
        <div class="row mt-2">
          <div class="col-6">
            <KaTeXComponent :expression="'v_r = 0.38 \\lambda_s \\lambda \\phi_c \\min(\\sqrt{f\'_c}, 8)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">[Eq. 13.7]</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'v_r = ' + vr_13_7.toFixed(2) + ' \\text{ MPa}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>

        <!-- Governing v_r -->
        <div class="row mt-3">
          <div class="col-6">
            <KaTeXComponent :expression="'v_r = ' + vr.toFixed(2) + ' \\text{ MPa}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Governing shear stress resistance</p>
          </div>
        </div>

        

      </div>
    </div>
  </div>
</template>

<script>
  import TopbarNav from "@/components/TopbarNav.vue";
  import PunchingShearDiagram from './components/PunchingShearDiagram.vue';

  export default {
    name: "PunchingShear",
    components: {
      TopbarNav,
      PunchingShearDiagram
    },
    data: function () {
      return {
        // Concrete Properties
        phi_c: 0.65,
        fc_prime: 30,

        // Forces
        Fz: 100, // Factored shear force in kN
        Mx: 0, // Factored moment about x-axis in kN.m
        My: 0, // Factored moment about y-axis in kN.m

        // Column Dimensions
        column_width: 300, // Column width in mm
        column_depth: 300, // Column depth in mm

        // Slab Properties
        d: 150, // Effective depth in mm

        // Shear Stress Resistance Parameters
        lambda: 1.0,
        lambda_s: 1.0,
        alpha_s: "4",
        excluded_side: null,
        
      };
    },
    computed: {
      Ac: function() {
        return this.shear_perimeter_polygon
        .filter(seg => !seg.excluded)
        .reduce((sum, seg) => sum + seg.L * this.d, 0);
      },

      Jcx: function() {
        return this.shear_perimeter_polygon
          .filter(seg => !seg.excluded)
          .reduce((sum, seg) => {
            return sum + (seg.L * this.d / 3) * (
              seg.point_i.y ** 2 + seg.point_i.y * seg.point_j.y + seg.point_j.y ** 2
            );
          }, 0);
      },
      Jcy: function() {
        return this.shear_perimeter_polygon
          .filter(seg => !seg.excluded)
          .reduce((sum, seg) => {
            return sum + (seg.L * this.d / 3) * (
              seg.point_i.x ** 2 + seg.point_i.x * seg.point_j.x + seg.point_j.x ** 2
            );
          }, 0);
      },
      gamma_fx: function() {
        // b1 = column_width (x-direction), b2 = column_depth (y-direction)
        return 1 / (1 + (2 / 3) * Math.sqrt(this.column_width / this.column_depth));
      },

      gamma_fy: function() {
        // b1 = column_depth (y-direction), b2 = column_width (x-direction)
        return 1 / (1 + (2 / 3) * Math.sqrt(this.column_depth / this.column_width));
      },

      gamma_vx: function() {
        return 1 - this.gamma_fx;
      },

      gamma_vy: function() {
        return 1 - this.gamma_fy;
      },

      cx: function() {
        // centroid x = sum(L * x_mid) / sum(L)
        const active = this.shear_perimeter_polygon.filter(seg => !seg.excluded);
        const totalL = active.reduce((sum, seg) => sum + seg.L, 0);
        return active.reduce((sum, seg) => {
          const x_mid = (seg.point_i.x + seg.point_j.x) / 2;
          return sum + seg.L * x_mid;
        }, 0) / totalL;
      },

      cy: function() {
        // centroid y = sum(L * y_mid) / sum(L)
        const active = this.shear_perimeter_polygon.filter(seg => !seg.excluded);
        const totalL = active.reduce((sum, seg) => sum + seg.L, 0);
        return active.reduce((sum, seg) => {
          const y_mid = (seg.point_i.y + seg.point_j.y) / 2;
          return sum + seg.L * y_mid;
        }, 0) / totalL;
      },

      cx_max: function() {
        // Max distance from centroid to outermost fiber in x-direction
        const active = this.shear_perimeter_polygon.filter(seg => !seg.excluded);
        const allX = active.flatMap(seg => [seg.point_i.x, seg.point_j.x]);
        return Math.max(...allX.map(x => Math.abs(x - this.cx)));
      },

      cy_max: function() {
        // Max distance from centroid to outermost fiber in y-direction
        const active = this.shear_perimeter_polygon.filter(seg => !seg.excluded);
        const allY = active.flatMap(seg => [seg.point_i.y, seg.point_j.y]);
        return Math.max(...allY.map(y => Math.abs(y - this.cy)));
      },

      vf_axial: function() {
        // Shear stress from axial force only
        return (this.Fz * 1000) / this.Ac; // Convert kN to N for MPa
      },
      vf_Mx: function() {
        // Shear stress contribution from Mx
        return (this.gamma_vx * this.Mx * 1e6 * this.cy_max) / this.Jcx; // Convert kN.m to N.mm for MPa
      },
      vf_My: function() {
        // Shear stress contribution from My
        return (this.gamma_vy * this.My * 1e6 * this.cx_max) / this.Jcy; // Convert kN.m to N.mm for MPa
      },
      vf: function() {
        // Fz is in kN, b0 and d are in mm
        // Convert to MPa: (Fz * 1000 N) / (Ac mm²) = MPa
        const term1 = this.vf_axial;
        const term2 = this.vf_Mx;
        const term3 = this.vf_My;
        // Return max absolute combination
        return Math.max(
          Math.abs(term1 + term2 + term3),
          Math.abs(term1 + term2 - term3),
          Math.abs(term1 - term2 + term3),
          Math.abs(term1 - term2 - term3),
        );
      },
      corner_stresses: function() {
        const halfC1 = this.column_width / 2;
        const halfC2 = this.column_depth / 2;
        const halfD = this.d / 2;

        const corners = [
          { label: 'SW', x: -(halfC1 + halfD), y: -(halfC2 + halfD) },
          { label: 'SE', x:  (halfC1 + halfD), y: -(halfC2 + halfD) },
          { label: 'NE', x:  (halfC1 + halfD), y:  (halfC2 + halfD) },
          { label: 'NW', x: -(halfC1 + halfD), y:  (halfC2 + halfD) },
        ];

        const v_Fz = (this.Fz * 1000) / (this.b0 * this.d);

        return corners.map(pt => {
          const v_Fz_val = v_Fz;
          const v_Mx_val = (this.gamma_vx * this.Mx * 1e6 * (pt.y - this.cy)) / this.Jcx;
          const v_My_val = (this.gamma_vy * this.My * 1e6 * (pt.x - this.cx)) / this.Jcy;
          return {
            label: pt.label,
            x: pt.x,
            y: pt.y,
            v_Fz: v_Fz_val,
            v_Mx: v_Mx_val,
            v_My: v_My_val,
            v_total: v_Fz_val + v_Mx_val + v_My_val,
          };
        });
      },

      segment_stresses: function() {
        const cornerMap = {};
        this.corner_stresses.forEach(c => { cornerMap[c.label] = c; });

        const segCorners = {
          S: ['SW', 'SE'],
          E: ['SE', 'NE'],
          N: ['NE', 'NW'],
          W: ['NW', 'SW'],
        };

        return this.shear_perimeter_polygon.map(seg => {
          const [labelI, labelJ] = segCorners[seg.label];
          const ci = cornerMap[labelI];
          const cj = cornerMap[labelJ];
          return {
            label: seg.label,
            excluded: seg.excluded,
            point_i: seg.point_i,
            point_j: seg.point_j,
            L: seg.L,
            stress_i: {
              v_Fz:    ci.v_Fz,
              v_Mx:    ci.v_Mx,
              v_My:    ci.v_My,
              v_total: ci.v_total,
            },
            stress_j: {
              v_Fz:    cj.v_Fz,
              v_Mx:    cj.v_Mx,
              v_My:    cj.v_My,
              v_total: cj.v_total,
            },
          };
        });
      },
      beta_c: function() {
        // Ratio of long dimension to short dimension
        const long_dim = Math.max(this.column_width, this.column_depth);
        const short_dim = Math.min(this.column_width, this.column_depth);
        return long_dim / short_dim;
      },
      sqrt_fc_capped: function() {
        // 13.3.4.2 limit - cap sqrt(f'c) at 8
        return Math.min(Math.sqrt(this.fc_prime), 8);
      },
      vr_13_5: function() {
        // Equation 13.5
        return (1 + 2 / this.beta_c) * 0.19 * this.lambda_s * this.lambda * this.phi_c * this.sqrt_fc_capped;
      },
      b0: function() {
      // Perimeter of the shear area used in calculation of alpha_s term in Eq. 13.6
        return this.Ac / this.d
      },
      vr_13_6: function() {
        // Equation 13.6
        return (parseFloat(this.alpha_s) * this.d / this.b0 + 0.19) * this.lambda_s * this.lambda * this.phi_c * this.sqrt_fc_capped;
      },
      vr_13_7: function() {
        // Equation 13.7
        return 0.38 * this.lambda_s * this.lambda * this.phi_c * this.sqrt_fc_capped;
      },
      vr: function() {
        // Minimum of the three equations
        return Math.min(this.vr_13_5, this.vr_13_6, this.vr_13_7);
      },
      shear_perimeter_polygon: function() {
        const halfD = this.d / 2;
        const halfC1 = this.column_width / 2;
        const halfC2 = this.column_depth / 2;

        const x = halfC1 + halfD;
        const y = halfC2 + halfD;

        const points = [
          { x: -x, y: -y }, // SW
          { x:  x, y: -y }, // SE
          { x:  x, y:  y }, // NE
          { x: -x, y:  y }, // NW
        ];

        const segments = [
          { label: "S", i: 0, j: 1 },
          { label: "E", i: 1, j: 2 },
          { label: "N", i: 2, j: 3 },
          { label: "W", i: 3, j: 0 },
        ];

        return segments.map(seg => {
          const p1 = points[seg.i];
          const p2 = points[seg.j];
          const L = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
          const excluded = this.excluded_side
            ? this.excluded_side.includes(seg.label)
            : false;
          return {
            label: seg.label,
            point_i: p1,
            point_j: p2,
            L,
            excluded,
          };
        });
      },
    },
    watch: {
      alpha_s: function() {
        if (this.alpha_s === "3") {
          this.excluded_side = "N";
        } else if (this.alpha_s === "2") {
          this.excluded_side = "NE";
        } else {
          this.excluded_side = null;
        }
      },
    }
  };
</script>

<style scoped>
.engsheet-table {
  font-size: 0.7rem;
  border-collapse: collapse;
  width: 100%;
  margin-top: 10px;
  background-color: #fff;
}

.engsheet-table th, .engsheet-table td {
  text-align: center;
  vertical-align: middle;
  padding: 2px 4px;
  border: 1px solid #999;
  background-color: #fff !important;
  min-width: 35px;
}

.engsheet-table th {
  font-weight: bold;
  background-color: #fff !important;
}

.engsheet-table td {
  font-weight: normal;
  background-color: #fff !important;
}

</style>
